import SmimeContext from '../store/schema/SmimeContext';
import getSmimeInfobarMessageFromErrorCode from './getSmimeInfobarMessageFromErrorCode';
import SmimeInstallationStatus from 'owa-smime-adapter/lib/store/schema/SmimeInstallationStatus';
import { getStore } from 'owa-smime-adapter/lib/store/store';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';
import {
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

const getSmimeControlErrorInfobarMessage = (
    key: string,
    context: SmimeContext,
    isItemSigned: boolean,
    isCompose: boolean,
    isReply: boolean,
    isForward: boolean
): InfoBarMessageViewState => {
    const errorCode: SmimeErrorStateEnum = getSmimeControlErrorCode(
        isItemSigned,
        isCompose,
        isReply,
        isForward
    );

    if (errorCode !== SmimeErrorStateEnum.NoError) {
        const messageParts = getSmimeInfobarMessageFromErrorCode(errorCode, context);
        return {
            key,
            source: isCompose ? InfoBarMessageSource.Compose : InfoBarMessageSource.ReadingPane,
            rank:
                errorCode === SmimeErrorStateEnum.ControlOutOfDateCompatible
                    ? InfoBarMessageRank.Info
                    : InfoBarMessageRank.Error,
            messageParts: messageParts,
        };
    }

    return null;
};

/**
 * @param isItemSigned   - isItemSigned: true means it is either clear sign/opaque signed.
 * @param isCompose - isCompose: true means the source of request for infobar is compose.
 */
export const getSmimeControlErrorCode = (
    isItemSigned: boolean,
    isCompose: boolean,
    isReply: boolean,
    isForward: boolean
): SmimeErrorStateEnum => {
    const smimeInstallationStatus = getStore().installationStatus;
    let errorCode: SmimeErrorStateEnum;
    switch (smimeInstallationStatus) {
        case SmimeInstallationStatus.Unknown:
        case SmimeInstallationStatus.Installed:
            errorCode = SmimeErrorStateEnum.NoError;
            break;
        // Plugin is compatible but out of date
        // This is the only case where both control and
        // message decode failure infoBars can coexist.
        case SmimeInstallationStatus.NotUpToDateCompatible:
            errorCode = SmimeErrorStateEnum.ControlOutOfDateCompatible;
            break;
        // Control exist but it is out of date
        // and cannot be used.
        case SmimeInstallationStatus.NotUpToDateForce:
            if (isReply || isForward) {
                errorCode = SmimeErrorStateEnum.ControlOutOfDateReplyForward;
            } else if (isCompose) {
                errorCode = SmimeErrorStateEnum.ControlOutOfDateCompatible;
            } else if (isItemSigned) {
                errorCode = SmimeErrorStateEnum.ControlOutOfDateSigned;
            } else {
                errorCode = SmimeErrorStateEnum.ControlOutOfDateCantOpenMessage;
            }
            break;
        // In this mode the plugin exists
        // however it will always return error
        // because domain is not allowed.
        case SmimeInstallationStatus.DomainNotAllowedExtensionOptionPage:
        case SmimeInstallationStatus.DomainNotAllowedRestart:
            errorCode = SmimeErrorStateEnum.DomainRejectedError;
            break;
        case SmimeInstallationStatus.BrowserNotSupported:
            if (isReply) {
                errorCode =
                    SmimeErrorStateEnum.ControlNotSupportedOnReplyToMessageWithoutOptionToInstall;
            } else if (isForward) {
                errorCode =
                    SmimeErrorStateEnum.ControlNotSupportedOnForwardOfMessageWithoutOptionToInstall;
            } else if (isCompose) {
                errorCode = SmimeErrorStateEnum.ControlNotInstalledOnComposeWithoutOptionToInstall;
            } else if (isItemSigned) {
                errorCode = SmimeErrorStateEnum.ControlNotSupportedOnSignedMessage;
            } else {
                errorCode = SmimeErrorStateEnum.ControlNotSupported;
            }
            break;
        case SmimeInstallationStatus.MissingExtensionByAdmin:
            if (isCompose) {
                errorCode =
                    SmimeErrorStateEnum.ExtensionNotInstalledOnComposeWithOptionToInstallByAdmin;
            } else if (isItemSigned) {
                errorCode =
                    SmimeErrorStateEnum.ExtensionNotInstalledOnSignedMessageWithOptionToInstallByAdmin;
            } else {
                errorCode =
                    SmimeErrorStateEnum.ExtensionNotInstalledOnEncryptedMessageWithOptionToInstallByAdmin;
            }
            break;
        default:
            if (isCompose) {
                errorCode = SmimeErrorStateEnum.ControlNotInstalledOnComposeWithOptionToInstall;
            } else if (isItemSigned) {
                errorCode =
                    SmimeErrorStateEnum.ControlNotInstalledOnSignedMessageWithOptionToInstall;
            } else {
                errorCode =
                    SmimeErrorStateEnum.ControlNotInstalledOnEncryptedMessageWithOptionToInstall;
            }
            break;
    }
    return errorCode;
};

export default getSmimeControlErrorInfobarMessage;
